import {ParentSize} from '@visx/responsive';
import React, {ReactNode, useMemo} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import uniqueId from 'util/uniqueId';

import {BenchlingChartContext, BenchlingChartProperties} from './BenchlingChartContext';
import {BENCHLING_CHART_MARGIN} from './utils/Theme';

const BENCHLING_CHART_SIZE_ADJUSTMENT = BENCHLING_CHART_MARGIN * 2;

interface BenchlingChartProps {
  children: (props: BenchlingChartProperties) => ReactNode;
  dataTestAttrs?: DataTestAttrs;
}

const PARENT_SIZE_STYLES: React.CSSProperties = {
  height: '100%',
  width: '100%',
  margin: BENCHLING_CHART_MARGIN,
};

/**
 * Wrapper component for all charts.
 *
 * Adds responsiveness to wrapped components.
 *
 * In the future, this can be extended with any additional functionality we may want to share across all charts.
 *
 * @deprecated Charts should be created through backend Chart model and rendered through /charts/chart-content module. Reach out
 * to Insights team for more details on charting.
 */
export default function BenchlingChart({children, dataTestAttrs}: BenchlingChartProps): JSX.Element {
  const clientId = useMemo(() => uniqueId('benchling-chart-'), []);

  return (
    <ParentSize {...getTestProps(dataTestAttrs)} parentSizeStyles={PARENT_SIZE_STYLES}>
      {({height, width}) =>
        width > 0 && height > BENCHLING_CHART_MARGIN ? (
          <BenchlingChartContext.Provider
            // There seems to be an odd case where when putting a `BenchlingChart` in a container that's also responsive
            // to it's container's height where we get an infinite loop of `ParentSize` recalculating itself and the parent
            // container becoming slightly too big, which triggers `ParentSize` to recalculate itself and the parent to
            // grow again causing an infinite resize loop. The result is that the chart looks like it's growing continuously.
            //
            // The best workaround is to make the parent limit the height (e.g. https://github.com/airbnb/visx/issues/881),
            // and we've employed that in Insights. The issue is that we will keep running into this issue when we render
            // charts in new contexts, so we should fix it on the framework level. The best work around in this case,
            // is to make the size of the parent div that `ParentSize` uses to calculate with `ResizeObserver` be
            // slightly less than 100%. That way the size won't infinitely grow and we can render our charts in any
            // context.
            // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
            value={{
              clientId,
              width: width - BENCHLING_CHART_SIZE_ADJUSTMENT,
              height: height - BENCHLING_CHART_SIZE_ADJUSTMENT,
            }}
          >
            {children({
              clientId,
              width: width - BENCHLING_CHART_SIZE_ADJUSTMENT,
              height: height - BENCHLING_CHART_SIZE_ADJUSTMENT,
            })}
          </BenchlingChartContext.Provider>
        ) : null
      }
    </ParentSize>
  );
}
